import { useEffect, useMemo, useState } from "react";
import { Box, Button, Flex, IconButton, Input, Select, Spinner, Text, Tooltip, useToast } from "@chakra-ui/react";
import { FaLink, FaUnlink } from "react-icons/fa";
import { crm, Pill } from "../../../theme";
import { BillingSetting, billingKindLabels } from "../../../models/BillingSetting";
import { getBillingSettings, linkBillingSettingClient } from "../../../services/billingSettingService";
import { toneColors } from "../../process/components/duimp";
import { settingLabel } from "../../simulator/ClientBranchPicker";

const UNIT_LABELS: Record<number, string> = { 1: "Logística", 2: "Distribuidora", 3: "Rondônia", 4: "W Trading" };

const percent = (value?: number | null) =>
  value == null ? "—" : `${value.toLocaleString("pt-BR", { maximumFractionDigits: 4 })}%`;

/**
 * Parametrizações fiscais (modelo Gett) ligadas ao cliente. O simulador oferece primeiro as do cliente.
 * Ligar/desligar salva na hora (não depende do "Salvar alterações").
 */
const ClientBillingSettings = ({ commercialClientId }: { commercialClientId: string }) => {
  const toast = useToast();
  const [settings, setSettings] = useState<BillingSetting[] | null>(null);
  const [search, setSearch] = useState("");
  const [pick, setPick] = useState("");
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    getBillingSettings()
      .then((list) => active && setSettings(list))
      .catch(() => active && setSettings([]));
    return () => {
      active = false;
    };
  }, []);

  const linked = useMemo(() => (settings ?? []).filter((s) => s.commercialClientId === commercialClientId), [settings, commercialClientId]);
  const candidates = useMemo(() => {
    const term = search.trim().toLowerCase();
    return (settings ?? [])
      .filter((s) => s.commercialClientId !== commercialClientId && (s.kind === "encomenda" || s.kind === "contaOrdem" || s.kind === "venda"))
      .filter((s) => !term || s.name.toLowerCase().includes(term))
      .slice(0, 200);
  }, [settings, search, commercialClientId]);

  const link = async (setting: BillingSetting, clientId: string | null) => {
    setBusy(setting.id!);
    try {
      const result = await linkBillingSettingClient(setting.id!, clientId);
      if (!result.success) {
        toast({ status: "error", title: result.notifications?.[0] ?? "Não foi possível salvar" });
        return;
      }
      setSettings((list) => (list ?? []).map((s) => (s.id === setting.id ? { ...s, commercialClientId: clientId } : s)));
      setPick("");
    } catch {
      toast({ status: "error", title: "Não foi possível salvar" });
    } finally {
      setBusy(null);
    }
  };

  const picked = candidates.find((s) => s.id === pick);

  return (
    <Box mt="22px">
      <Text fontSize="sm" color={crm.textMuted}>Parametrizações fiscais</Text>
      <Text fontSize="xs" color={crm.textFaint} mb="8px">
        Do Gett (Faturamento › Parametrizações). O simulador usa ICMS, ST/MVA, DIFAL e IBS/CBS daqui, com as exceções por NCM e UF.
      </Text>
      {settings == null ? (
        <Spinner size="sm" />
      ) : (
        <>
          {linked.length === 0 && <Text fontSize="sm" color={crm.textFaint} py="4px">Nenhuma parametrização ligada.</Text>}
          {linked.map((s) => (
            <Flex key={s.id} align="center" gap="8px" border="1px solid" borderColor={crm.border} borderRadius="10px" p="8px 10px" mb="6px">
              <Box flex="1" minW="0">
                <Text fontSize="sm" fontWeight="600" noOfLines={1} title={s.name}>{s.name}</Text>
                <Flex gap="6px" mt="4px" wrap="wrap">
                  <Pill {...toneColors.neutral}>{billingKindLabels[s.kind]}</Pill>
                  {s.unitType && <Pill {...toneColors.neutral}>{UNIT_LABELS[s.unitType]}{s.unitState ? ` ${s.unitState}` : ""}</Pill>}
                  <Pill {...toneColors.neutral}>ICMS {percent(s.rates?.icmsPercent)}</Pill>
                  {s.rates?.cfop && <Pill {...toneColors.neutral}>CFOP {s.rates.cfop}</Pill>}
                  {s.exceptionCount > 0 && <Pill {...toneColors.success}>{s.exceptionCount} exceções</Pill>}
                </Flex>
              </Box>
              <Tooltip label="Desligar do cliente">
                <IconButton size="sm" variant="ghost" aria-label="Desligar" icon={<FaUnlink />} isLoading={busy === s.id} onClick={() => link(s, null)} />
              </Tooltip>
            </Flex>
          ))}
          <Flex gap="8px" mt="8px" wrap="wrap">
            <Input size="sm" flex="1 1 160px" placeholder="Filtrar parametrizações" value={search} onChange={(e) => setSearch(e.target.value)} />
            <Select size="sm" flex="2 1 220px" placeholder="Ligar parametrização…" value={pick} onChange={(e) => setPick(e.target.value)}>
              {candidates.map((s) => (
                <option key={s.id} value={s.id}>
                  {settingLabel(s)}{s.clientName ? ` (hoje: ${s.clientName})` : ""}
                </option>
              ))}
            </Select>
            <Button size="sm" leftIcon={<FaLink />} isDisabled={!picked} isLoading={!!picked && busy === picked.id} onClick={() => picked && link(picked, commercialClientId)}>
              Ligar
            </Button>
          </Flex>
          {picked?.clientName && (
            <Text fontSize="xs" color={crm.warning} mt="4px">Ligar move a parametrização de {picked.clientName} para este cliente.</Text>
          )}
        </>
      )}
    </Box>
  );
};

export default ClientBillingSettings;
